import { FC, useEffect, useState, useRef } from "react";
import { Download01 } from "untitledui-js-base";
import { CSVLink } from "react-csv";

import { statementActivity } from "../types";
import { PaginatedResponse } from "shared/types";
import { Statement, StatementStatus } from "../../statements/types";
import { Loader } from "shared/components/Loader";

import { Button, ButtonSize, ButtonVariant } from "shared/components";
import FeeDisplay from "shared/components/FeeDisplay";

interface DownloadButtonsProps {
  statementId: string;
  searchQuery: string;
  billingLineItems: PaginatedResponse<statementActivity>;
  getCSVLineItems: (statementId: string, search: string) => Promise<any>;
  activeTab: string;
}

const DownloadButtons: FC<DownloadButtonsProps> = ({
  statementId,
  searchQuery,
  getCSVLineItems,
  activeTab
}) => {
  const [csvData, setCsvData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const ref = useRef<HTMLAnchorElement>(null);

  const handleDownloadCSV = async () => {
    setIsLoading(true);
    const csvFormattedData = await getCSVLineItems(statementId, searchQuery);
    setCsvData(csvFormattedData);
    setIsLoading(false);
  };

  useEffect(() => {
    if (csvData.length)
      // @ts-ignore
      ref.current?.link.click() && ref.current.link.blur();
  }, [csvData]);

  return (
    <div className="flex justify-end">
      {isLoading ? (
        <div className="flex items-center mr-6">
          <Loader size={25} />
        </div>
      ) : (
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          className="mr-2"
          onClick={handleDownloadCSV}
          loading={isLoading}
          Icon={Download01}
        >
          Download CSV
        </Button>
      )}

      {activeTab === "Summary" ? (
        <Button
          Icon={Download01}
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => window.print()}
        >
          Download PDF
        </Button>
      ) : null}
      <CSVLink
        ref={ref}
        data={csvData}
        filename="statement.csv"
        target="_blank"
      />
    </div>
  );
};

interface BillingDetailHeaderProps {
  statementId: string;
  searchQuery: string;
  statementDetails: Statement;
  statementActivities: PaginatedResponse<statementActivity>;
  getCSVLineItems: (statementId: string, search: string) => Promise<any>;
  activeTab: string;
}

export const BillingDetailHeader = ({
  statementId,
  searchQuery,
  statementDetails,
  statementActivities,
  getCSVLineItems,
  activeTab
}: BillingDetailHeaderProps) => {
  return (
    <div className="no-print flex items-center justify-between">
      <div className="flex w-full items-center justify-between py-6">
        <div className="flex items-center">
          <span className="text-xl font-bold capitalize text-content-1000">
            Statement
          </span>
          <span className="x-2 mx-2 inline-block h-1.5 w-1.5 rounded-full bg-alpha-80"></span>
          <FeeDisplay
            feeInCents={statementDetails.total.cents}
            currency={statementDetails.total.currency}
            className="pr-4 text-xl font-bold !text-content-1000"
          />
          <span
            className={`rounded-xl px-2 py-1 text-sm font-medium ${
              statementDetails.status === StatementStatus.Finished
                ? "bg-success-50 text-success-700"
                : "bg-primary-20 text-primary-600"
            }`}
          >
            {statementDetails.status === StatementStatus.Finished
              ? "Paid"
              : "Unpaid"}
          </span>
        </div>
        <DownloadButtons
          billingLineItems={statementActivities}
          statementId={statementId}
          searchQuery={searchQuery}
          getCSVLineItems={getCSVLineItems}
          activeTab={activeTab}
        />
      </div>
    </div>
  );
};
